<template>
    <div
        class="cz2__two_pack_selector"
        role="region"
        aria-label="Building your Custom Pop"
    >
        <!-- SPELLING BEE START: Borders and logos -->
        <template v-if="isSpellingBee">
            <div class="cz2__sb-border" />
            <div class="cz2__sb-background" />

            <div
                class="cz2__sb-logo-m-two-pack"
                @click.prevent="tapLogo"
            />

            <div
                class="cz2__sb-logo-d-two-pack"
                @click.prevent="tapLogo"
            />

            <div
                class="cz2__sb-logo-k-two-pack"
                @click.prevent="tapLogo"
            />
        </template>
        <!-- SPELLING BEE END -->

        <ul
            :class="['cz2__two_pack_selector__content', {
                'is-mobile': isMobile,
            }]"
        >
            <li>
                <button
                    ref="onePackButton"
                    @click="$emit('start', 'one-pack')"
                    class="cz2__two_pack_selector__button"
                >
                    <div
                        class="cz2__two_pack_selector__content__image-container"
                        :style="{
                            backgroundImage: `url(${onePackImage})`,
                        }"
                    />

                    <!-- SPELLING BEE START: Replace buttons. -->
                    <sb-banner-button
                        v-if="isSpellingBee"
                        class="sb-banner-button"
                        as="div"
                    >
                        {{ $t('ONE_PACK_SB') }}
                    </sb-banner-button>
                    <!-- SPELLING BEE END -->

                    <red-banner-button
                        v-else
                        as="div"
                        class="cz2__btn--two-pack-red-banner"
                    >
                        {{ $t('ONE_PACK') }}
                    </red-banner-button>

                    <!-- SPELLING BEE START: Replace buttons. -->
                    <sb-button
                        v-if="isSpellingBee"
                        class="sb-button-width"
                        heightVariant="large"
                        as="div"
                    >
                        {{$t('GET_STARTED')}}
                    </sb-button>
                    <!-- SPELLING BEE END -->

                    <btn
                        v-else
                        as="span"
                        class="cz2__btn--narrow cz2__btn--secondary"
                    >
                        <span>
                            {{$t('GET_STARTED')}}
                        </span>
                    </btn>
                </button>
            </li>
            <li>
                <button
                    ref="twoPackButton"
                    @click="$emit('start', 'two-pack')"
                    class="cz2__two_pack_selector__button"
                >
                    <div
                        class="cz2__two_pack_selector__content__image-container"
                        :style="{
                            backgroundImage: `url(${twoPackImage})`,
                        }"
                    />

                    <!-- SPELLING BEE START: Replace buttons. -->
                    <sb-banner-button
                        v-if="isSpellingBee"
                        as="div"
                    >
                        {{ $t('TWO_PACK_SB') }}
                    </sb-banner-button>
                    <!-- SPELLING BEE END -->

                    <red-banner-button
                        v-else
                        as="div"
                        class="cz2__btn--two-pack-red-banner"
                    >
                        {{ $t('TWO_PACK') }}
                    </red-banner-button>

                    <!-- SPELLING BEE START: Replace buttons. -->
                    <sb-button
                        v-if="isSpellingBee"
                        class="sb-button-width"
                        heightVariant="large"
                        as="div"
                    >
                        {{$t('GET_STARTED')}}
                    </sb-button>
                    <!-- SPELLING BEE END -->

                    <btn
                        v-else
                        as="span"
                        class="cz2__btn--narrow cz2__btn--secondary"
                    >
                        <span>
                            {{$t('GET_STARTED')}}
                        </span>
                    </btn>
                </button>
            </li>
        </ul>

        <img
            src="../assets/images/toty-logo.png"
            :alt="$t('LOGO_TOTY')"
            class="cz2__toty-logo"
        >
    </div>
</template>

<script>
    import { animate } from 'motion';
    import MainNavigationListButton from './MainNavigationListButton.vue';
    import RedBannerButton from './RedBannerButton.vue';
    import Btn from './ui/Btn.vue';
    import SbButton from './SbButton.vue';
    import SbBannerButton from './SbBannerButton.vue';

    export default {
        name: 'TwoPackSelector',

        components: {
            Btn,
            MainNavigationListButton,
            SbBannerButton,
            RedBannerButton,
            SbButton,
        },

        computed: {
            globalSettings() {
                return this.$store.state.customizer.globalSettings?.blueprint?.custom;
            },

            onePackImage() {
                return `${this.globalSettings?.['fun-splash-one-pack-image']}?fmt=webp&qlt=70`;
            },

            twoPackImage() {
                return `${this.globalSettings?.['fun-splash-two-pack-image']}?fmt=webp&qlt=70`;
            },

            isSpellingBee() {
                return this.$store.state.isSpellingBee;
            },

            isMobile() {
                return this.$store.state.customizer.isMobile;
            },
        },

        mounted() {
            this.animateButtons();
        },

        methods: {
            animateButtons() {
                const { onePackButton, twoPackButton } = this.$refs;

                [onePackButton, twoPackButton].forEach((button) => {
                    animate(button, {
                        transform: [
                            'scale(0)',
                            'scale(0.6)',
                            'scale(1)',
                            'scale(1.05)',
                            'scale(1)',
                            'scale(0.98)',
                            'scale(1)',
                        ],
                    }, {
                        duration: 1,
                        easing: 'ease-in-out',
                    });
                });
            },

            tapLogo() {
                this.$store.dispatch('tapLogo');
            },
        },
    };
</script>

<style lang="scss">
    @import '../style/variables';

    #cz2.cz2 {
        .cz2__sb-logo-m-two-pack, .cz2__sb-logo-d-two-pack, .cz2__sb-logo-k-two-pack {
            display: none;
        }

        .cz2__two_pack_selector {
            position: absolute;

            top: 0;
            left: 0;
            right: 0;
            bottom: 0;

            width: 100%;
            height: 100%;

            display: flex;
            justify-content: center;
            align-items: center;
            background: rgba(0, 0, 0, 0);

            .cz2__toty-logo {
                top: 4px;
                right: 4px;
            }
        }

        .cz2__two_pack_selector__content {
            display: flex;
            flex-direction: column;

            justify-content: center;
            align-items: center;

            gap: 40px;

            @include respond-above(lg) {
                flex-direction: row;

                gap: 130px;
            }
        }

        .cz2__two_pack_selector__button {
            position: relative;

            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;

            // Size control.
            width: 200px;

            img {
                width: 100%;
                height: auto;
            }

            .cz2__btn--secondary {
                // Size control.
                margin-top: 15px;

                min-width: 70%;
                max-width: 302px;
                height: 50px;

                font-size: 16px;

                &.cz2__btn--narrow:hover {
                    font-size: 19px;
                }
            }
        }

        .cz2__two_pack_selector__content__image-container {
            position: relative;

            // Push it under the slanted button.
            top: 10px;

            width: 100%;

            // Give it size so the loading would be more smooth.
            aspect-ratio: 1.222;

            background-color: transparent;
            background-size: cover;
        }

        .cz2__btn--two-pack-red-banner {
            // Size control.
            font-size: 33px;
            line-height: 1.5;

            padding: 0 0;
        }

        @include respond-above(lg) {
            .cz2__two_pack_selector__button {
                width: 364px;

                .cz2__btn--secondary {
                    margin-top: 20px;
                }
            }

            .cz2__btn--two-pack-red-banner {
                font-size: 45px;
            }
        }

        @include respond-above(md) {
            .cz2__two_pack_selector__button {
                width: 364px;
            }
        }

        @include respond-above(xl) {
            .cz2__two_pack_selector__button {
                width: 450px;

                .cz2__btn--secondary {
                    margin-top: 35px;
                }
            }

            .cz2__btn--two-pack-red-banner {
                font-size: 64px;
            }
        }

        @include respond-above(2xl) {
            .cz2__two_pack_selector__button {
                width: 570px;
            }

            .cz2__btn--two-pack-red-banner {
                font-size: 80px;
            }
        }
    }

    #cz2.cz2.cz2__is-kiosk {
        .cz2__two_pack_selector__content {
            flex-direction: column;
        }

        .cz2__two_pack_selector__button {
            width: 678px;

            .cz2__btn--secondary {
                margin-top: 35px;

                font-size: 22px;

                &.cz2__btn--narrow:hover {
                    font-size: 26px;
                }
            }
        }

        .cz2__btn--two-pack-red-banner {
            font-size: 90px;
        }

        .cz2__two_pack_selector {
            .cz2__toty-logo {
                top: 4px;
                left: 4px;
                width: 239px;
                height: 239px;
            }
        }
    }

    ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    //   ▗▄▄▖▗▄▄▖ ▗▄▄▄▖▗▖   ▗▖   ▗▄▄▄▖▗▖  ▗▖ ▗▄▄▖    ▗▄▄▖ ▗▄▄▄▖▗▄▄▄▖
    //  ▐▌   ▐▌ ▐▌▐▌   ▐▌   ▐▌     █  ▐▛▚▖▐▌▐▌       ▐▌ ▐▌▐▌   ▐▌
    //   ▝▀▚▖▐▛▀▘ ▐▛▀▀▘▐▌   ▐▌     █  ▐▌ ▝▜▌▐▌▝▜▌    ▐▛▀▚▖▐▛▀▀▘▐▛▀▀▘
    //  ▗▄▄▞▘▐▌   ▐▙▄▄▖▐▙▄▄▖▐▙▄▄▖▗▄█▄▖▐▌  ▐▌▝▚▄▞▘    ▐▙▄▞▘▐▙▄▄▖▐▙▄▄
    ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    #cz2.cz2__is-sb {
        .cz2__two_pack_selector__content__image-container {
            background-size: contain;
            background-repeat: no-repeat;
            background-position: center;
            width: 100%;
            height: 100%;
            max-height: 18%;
            @include respond-above(lg) {
                max-height: 40%;
            }
        }

        .cz2__splash__logo {
            display: none;
        }

        .cz2__two_pack_selector__content {
            @include respond-above(lg) {
                margin-top: 100px;
                max-height: 60%;
            }

            margin-top: 20px;
            gap: 8px;
        }

        .sb-button-width {
            min-width: 275px;
            width: 70%;
            max-width: 302px;
        }

        .cz2__sb-border {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;

            border-image-source: url("../assets/images/branded/sb-final/SB-Border 1_M@2x.webp");

            border-width: 40px;
            border-image-width: 40px;
            border-image-slice: 80 fill;
            border-image-repeat: stretch;

            padding: 40px 40px;

            @include respond-above(lg) {
                border-image-source: url("../assets/images/branded/sb-final/SB-Border 2A_D@2x.webp");

                border-width: 95px;
                border-image-width: 95px;
                border-image-slice: 190 fill;
                border-image-repeat: stretch;

                padding: 95px 95px;
            }
        }

        .cz2__sb-background {
            background-image: url("../assets/images/branded/sb-final/SB-Background_pattern_3_M@2x.jpg");

            background-repeat: repeat;
            background-size: 100%;
            background-position: center;
            background-origin: border-box;
            background-color: #fff;

            position: absolute;

            left: 35px;
            top: 35px;

            height: calc(100% - 70px);
            width: calc(100% - 70px);

            @include respond-above(lg) {
                left: 70px;
                top: 70px;

                height: calc(100% - 140px);
                width: calc(100% - 140px);

                background-image: url("../assets/images/branded/sb-final/SB-Background_pattern_4_D@2x.jpg");
            }
        }

        .cz2__sb-logo-m-two-pack, .cz2__sb-logo-d-two-pack, .cz2__sb-logo-k-two-pack {
            display: none;
            position: absolute;
            left: 0;
            right: 0;
            margin: auto;
        }

        .cz2__sb-logo-m-two-pack {
            display: block;

            width: 262px;
            top: 50px;

            @include respond-above(lg) {
                display: none;
            }
        }

        .cz2__sb-logo-d-two-pack {
            @include respond-above(lg) {
                top: 10%;
                display: block;
                background-image: url("../assets/images/branded/sb-final/SB-Logo_1_D@2x.webp");
                background-size: contain;
                background-repeat: no-repeat;
                background-position: top;
                margin-top: 30px;
                height: 100%;
                max-height: 100px;
            }

            @include respond-above-height(lg, short) {
                max-height: 120px;
            }

            @include respond-above-height(lg, medium) {
                max-height: 160px;
            }
        }

        .cz2__sb-logo-m-two-pack {
            top: 7%;
            display: block;
            background-image: url("../assets/images/branded/sb-final/SB-Logo_1_M@2x.webp");
            background-size: contain;
            background-repeat: no-repeat;
            background-position: top;
            height: 100%;
            width: 100%;
            max-height: 60px;

            @include respond-above(lg) {
                display: none;
            }
        }

        @include respond-above(lg) {
            .cz2__two_pack_selector__button {
                width: 364px;

                .cz2__btn--secondary {
                    margin-top: 20px;
                }
            }

            .cz2__btn--two-pack-red-banner {
                font-size: 45px;
            }
        }

        @include respond-above(md) {
            .cz2__two_pack_selector__button {
                width: 200px;
            }
        }

        @include respond-above(lg) {
            .cz2__two_pack_selector__button {
                width: 364px;
            }
        }

        @include respond-above-height(xl, medium) {
            .cz2__two_pack_selector__button {
                width: 450px;

                .cz2__btn--secondary {
                    margin-top: 35px;
                }
            }

            .cz2__btn--two-pack-red-banner {
                font-size: 64px;
            }
        }

        @include respond-above-height(2xl, medium) {
            .cz2__two_pack_selector__button {
                width: 500px;
            }

            .cz2__btn--two-pack-red-banner {
                font-size: 80px;
            }
        }
    }

    ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // SPELLING BEE: KIOSK
    ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    #cz2.cz2__is-sb.cz2__is-kiosk {
        .cz2__two_pack_selector {
            overflow: hidden;
        }

        .cz2__sb-logo-m-two-pack, .cz2__sb-logo-d-two-pack {
            display: none;
        }

        .cz2__sb-logo-k-two-pack {
            top: 7%;
            display: block;
            background-image: url("../assets/images/branded/sb-final/SB-Logo_1_K@2x.webp");
            background-size: contain;
            background-repeat: no-repeat;
            background-position: top;
            height: 100%;
            width: 100%;
            max-width: 60%;
        }

        .cz2__sb-logo-m-two-pack, .cz2__sb-logo-d-two-pack {
            display: none;
        }

        .cz2__sb-background {
            background-image: url("../assets/images/branded/sb-final/SB-Background_pattern_4_K@2x.jpg");
        }
    }
</style>
